import React from "react"
import { useId } from "@reach/auto-id"
import { useTranslation } from "react-i18next"
import { v4 as uuidv4 } from "uuid"
import { isMobileOnly } from "react-device-detect"

import { useAlert } from "hooks"
import {
	DocumentToUpload,
	UploadedDocument,
	DocumentsTypes,
	ErrorResponse,
	FormError,
} from "models"
import { uploadFile, convertFileToBlob, isEmpty } from "utils"

import { Spinner, AddIcon } from "components/Elements"
import {
	getStatesAndHelpersInputFile,
	DEFAULT_ACCEPTED_EXTENSIONS,
	DEFAULT_MAX_FILE_SIZE,
	FileErrorActionType,
	Errors,
} from "components/Form"
import { HiddenInputFile } from "components/Form/File/Partials"
import { ButtonDeleteFile } from "./Partials"

import "./FileList.scoped.scss"
import { MobileSpinner } from "components/Elements/MobileSpinner"

const fileListStatusEnum = {
	loading: "loading",
	ready: "ready",
}

export type FileListProps = {
	id: string
	name: string
	label: string
	disabled?: boolean
	readonly?: boolean
	className?: string
	buttonClassName?: string
	// NB: defaultValue shows an existing remote file
	defaultValue?: UploadedDocument
	disabledLink?: boolean
	// Documents management
	files: UploadedDocument[]
	refreshFiles: () => void
	maxFilesToUpload?: number
	filesType: DocumentsTypes
	acceptedExtensions?: string[]
	maxFileSize?: number
	payloadData: {
		bike_id: string
		account_id: string
	}
}

export const FileList: React.FC<FileListProps> = React.forwardRef(
	(
		{
			// Attributes
			id,
			name,
			label = "",
			disabled,
			readonly,
			// Styles
			className = "",
			buttonClassName = "",
			// Error management
			acceptedExtensions = DEFAULT_ACCEPTED_EXTENSIONS,
			maxFileSize = DEFAULT_MAX_FILE_SIZE,
			// Documents managements
			files = [],
			refreshFiles,
			maxFilesToUpload = null,
			filesType = DocumentsTypes.AUTRE,
			payloadData,
		},
		ref: React.Ref<HTMLInputElement>
	) => {
		const prefix = useId()
		const inputId = id ?? `${prefix}-${name}`
		const errorId = `${inputId}-error`

		const { alert } = useAlert()
		const { t } = useTranslation()

		const { fileErrorReducer, checkForFileError } = getStatesAndHelpersInputFile({
			acceptedExtensions,
			maxFileSize,
		})

		const filteredFiles = files.filter((file) => file.document_data.gender === filesType)
		const [fileListStatus, setFileListStatus] = React.useState<
			(typeof fileListStatusEnum)[keyof typeof fileListStatusEnum]
		>(fileListStatusEnum.ready)
		const [fileError, dispatchFileError] = React.useReducer(fileErrorReducer, null)

		const isLoading = fileListStatus === fileListStatusEnum.loading
		const maxFilesIsReached = maxFilesToUpload && filteredFiles.length >= maxFilesToUpload
		const noFilesAreAvailable = isEmpty(filteredFiles)

		const handleDocumentUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
			try {
				setFileListStatus(fileListStatusEnum.loading)
				const inputFileTarget = event.target as HTMLInputElement

				// the original type FileList is not considered as an array
				const file = [...(inputFileTarget.files as unknown as File[])][0]

				if (!file) throw new Error(t("error_default", { ns: "form" }))

				// To prevent error from uploading with Google Drive for exemple
				// Need to clone the file before check errors
				const buffer = await file.arrayBuffer()
				const cloneFile = new File([buffer], file.name, { type: file.type })

				const hasFileError = await checkForFileError(cloneFile)

				if (hasFileError)
					dispatchFileError({
						type: hasFileError.type,
					})

				if (!hasFileError) {
					const fileExtension = cloneFile.name.split(".").pop()
					const { bike_id, account_id } = payloadData
					const documentToUpload: DocumentToUpload = {
						bike_id,
						account_id,
						document: {
							name: `${uuidv4()}.${fileExtension}`,
							custom_name: cloneFile.name,
							gender: filesType,
						},
					}
					const blob = await convertFileToBlob(cloneFile)

					dispatchFileError({ type: FileErrorActionType.READY })
					await uploadFile(documentToUpload, blob)
					if (refreshFiles) refreshFiles()
				}
			} catch (error) {
				console.error(error)
				const errorMsg = (error as ErrorResponse)?.message?.toString()
				alert("error", errorMsg)
			} finally {
				setFileListStatus(fileListStatusEnum.ready)
			}
		}

		return (
			<div className={`c-filelist ${className}`}>
				{!noFilesAreAvailable && (
					<ul>
						{filteredFiles.map((file, index) => {
							return (
								<li
									className="c-filelist__item p-2"
									key={`${index}-${file.create_date}`}
								>
									<a
										href={file.document_data.url}
										className={"flex-1 text-s break-all"}
										target="_blank"
										rel="noopener noreferrer"
									>
										{file.document_data.custom_name}
									</a>
									<div>
										<ButtonDeleteFile
											file={file}
											className="ml-2 p-1"
											variant="secondary"
											size="small"
											onFileDelete={refreshFiles}
										/>
									</div>
								</li>
							)
						})}
					</ul>
				)}

				{!maxFilesIsReached && (
					<>
						<HiddenInputFile
							name={name}
							inputId={inputId}
							ref={ref}
							disabled={disabled}
							readonly={readonly}
							onChange={handleDocumentUpload}
						/>

						<label
							htmlFor={inputId}
							className={`
								c-filelist__button
								mt-4
								${isLoading ? "pointer-events-auto" : ""}
								${buttonClassName}
							`}
						>
							{!isLoading && (
								<>
									<AddIcon size="25" color="var(--secondaryColor)" />
									<div className="pl-3">{label}</div>
								</>
							)}

							{isLoading && !isMobileOnly && (
								<Spinner color={"var(--secondaryColor"} size={"20px"} />
							)}
						</label>
					</>
				)}

				{isLoading && isMobileOnly && (
					<MobileSpinner
						color={"var(--secondaryColor"}
						size={"70px"}
						isOpen={isLoading}
					/>
				)}

				{/* Error messages */}
				{fileError && (
					<Errors
						id={errorId}
						errors={fileError ? [fileError as unknown as FormError] : []}
					/>
				)}
			</div>
		)
	}
)
